import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, Copy, LoaderCircle, Mail, RefreshCw, Trash2, X } from "lucide-react";
import { api } from "../lib/api";
import { pixelInlineScript, pixelSnippetPretty, trackSnippet } from "./pixelSnippet";
import { PIXEL_GUIDES, type Guide, type GuideBlock } from "./pixelGuides";

// Custom pixel setup: install the snippet, check the pages ads will send
// traffic to, watch the first events arrive, and see which conversion events
// have fired. Built on the ad network's public API instead of its element so
// the flow stays under the portal's own design and copy.

type SiteState = "installed" | "missing" | "unreachable" | "foreign";
type Site = { id: string; url: string; hostname: string; state: SiteState; scoped: boolean | null; installed: boolean; reachable: boolean | null; last_seen_days: number | null; page_events: string[]; host_events: string[]; last_fired_days: Record<string, number>; checked_at: string | null; created_at: string };
type Website = { hostname: string; page_views: number; visitors: number; last_seen: string | null; daily: number[] };
type Overview = { account_id: string; status: { installed: boolean; last_seen_days: number | null; last_fired_days: Record<string, number>; firing_data_ok: boolean } | null; websites: Website[]; sites: Site[] };
type WebsiteEvents = { hostname: string; events: Array<{ event: string; count: number }>; last_fired_days: Record<string, number>; host_events: string[] };
type RecentEvent = { id?: string; event_name?: string; custom_name: string | null; event_time?: string; url: string | null; path: string | null };

const CONVERSION_EVENTS: Array<{ name: string; label: string; when: string; snippet?: string }> = [
  { name: "lead", label: "Lead", when: "A form or opt-in was submitted." },
  { name: "schedule", label: "Schedule", when: "A call or appointment was booked." },
  { name: "complete_registration", label: "Complete registration", when: "An account or webinar signup finished." },
  { name: "submit_application", label: "Submit application", when: "An application was sent." },
  { name: "contact", label: "Contact", when: "A contact request was made." },
  { name: "add_to_cart", label: "Add to cart", when: "Something was added to a cart." },
  { name: "view_content", label: "View content", when: "A key page was viewed." },
  { name: "purchase", label: "Purchase", when: "A sale completed on your checkout. Send the order value.", snippet: 'oxwell.track("purchase", { value: 149.99, currency: "USD" });' },
];


function eventLabel(name: string): string {
  const stripped = name.replace(/^pixel\./, "");
  if (stripped.startsWith("custom:")) return stripped.slice(7);
  return stripped.replace(/_/g, " ");
}

function daysAgo(days: number | null | undefined): string {
  if (days === null || days === undefined) return "never";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

function formatWhen(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return "";
  return new Date(parsed).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function CodeCard({ label, code }: { label: string; code: string }) {
  const [copied, setCopied] = useState(false);
  return <div className="pixel-codecard">
    <div><span>{label}</span><button type="button" className={copied ? "is-copied" : ""} onClick={() => { void navigator.clipboard.writeText(code).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1600); }); }}>{copied ? <><Check /> Copied</> : <><Copy /> Copy</>}</button></div>
    <pre>{code}</pre>
  </div>;
}

function StatusPill({ tone, children }: { tone: "idle" | "wait" | "ok" | "warn"; children: ReactNode }) {
  return <span className={`pixel-pill is-${tone}`}><i />{children}</span>;
}

function StateBadge({ state }: { state: SiteState }) {
  const copy = state === "installed" ? "Installed" : state === "foreign" ? "Wrong account" : state === "unreachable" ? "Unreachable" : "Not found";
  return <span className={`pixel-dot is-${state}`}><i />{copy}</span>;
}

// **bold** and `code` only; the guides use nothing else inline.
function Inline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return <>{parts.map((part, index) => part.startsWith("**") ? <strong key={index}>{part.slice(2, -2)}</strong> : part.startsWith("`") ? <code key={index}>{part.slice(1, -1)}</code> : <span key={index}>{part}</span>)}</>;
}

function GuideBlocks({ blocks, accountId }: { blocks: GuideBlock[]; accountId: string }) {
  return <>{blocks.map((block, index) => {
    if (block.type === "p") {
      if (block.text.startsWith("## ")) return <h3 key={index} className="pixel-guide__h">{block.text.slice(3)}</h3>;
      if (block.text.startsWith("### ")) return <h4 key={index} className="pixel-guide__h">{block.text.slice(4)}</h4>;
      return <p key={index}><Inline text={block.text} /></p>;
    }
    if (block.type === "list") return <ul key={index}>{block.items.map((item) => <li key={item}><Inline text={item} /></li>)}</ul>;
    if (block.type === "note") return <div key={index} className="pixel-guide__note"><Inline text={block.text} /></div>;
    if (block.type === "table") return <table key={index} className="pixel-guide__table"><thead><tr>{block.head.map((cell) => <th key={cell}><Inline text={cell} /></th>)}</tr></thead><tbody>{block.rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}><Inline text={cell} /></td>)}</tr>)}</tbody></table>;
    const code = block.type === "snippet"
      ? `<script>\n${pixelInlineScript(accountId)}${block.extra ? `\n\n${block.extra}` : ""}\n</script>`
      : block.code.replaceAll("{{ACCOUNT_ID}}", accountId);
    return <CodeCard key={index} label={block.label || (block.type === "snippet" ? "Pixel snippet" : block.lang || "code")} code={code} />;
  })}</>;
}

function GuideView({ guide, accountId }: { guide: Guide; accountId: string }) {
  return <div className="pixel-guide">
    <GuideBlocks blocks={guide.intro} accountId={accountId} />
    <ol className="pixel-guide__steps">
      {guide.steps.map((step) => <li key={step.title}><h4>{step.title}</h4><GuideBlocks blocks={step.blocks} accountId={accountId} /></li>)}
    </ol>
    <GuideBlocks blocks={guide.after} accountId={accountId} />
  </div>;
}

export function PixelSetup() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState("");
  const [urlDraft, setUrlDraft] = useState("");
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [eventKey, setEventKey] = useState("lead");
  const [openStep, setOpenStep] = useState<number | null>(null);
  const [copiedMain, setCopiedMain] = useState(false);
  const [copiedEvent, setCopiedEvent] = useState(false);
  const [guideKey, setGuideKey] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [hostEvents, setHostEvents] = useState<Record<string, WebsiteEvents | "loading" | "failed">>({});
  const [liveTest, setLiveTest] = useState<{ hostname: string; startedAt: string; events: RecentEvent[]; state: "waiting" | "seen" | "timeout" } | null>(null);
  const liveTimer = useRef(0);

  const load = useCallback(async () => {
    try { setData(await api<Overview>("/api/ads/pixel/overview")); setError(""); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "The pixel status could not be loaded."); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => () => { if (liveTimer.current) window.clearInterval(liveTimer.current); }, []);
  useEffect(() => {
    if (!guideKey) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setGuideKey(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [guideKey]);

  const checkUrl = async () => {
    const value = urlDraft.trim();
    if (!value) return;
    setChecking(true); setError("");
    try {
      await api<{ site: Site }>("/api/ads/pixel/sites", { method: "POST", body: JSON.stringify({ url: value }) });
      setUrlDraft("");
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "That page could not be checked."); }
    finally { setChecking(false); }
  };

  const recheck = async (site: Site) => {
    setBusy(`check:${site.id}`); setError("");
    try { await api(`/api/ads/pixel/sites/${encodeURIComponent(site.id)}/check`, { method: "POST" }); await load(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "The page could not be re-checked."); }
    finally { setBusy(""); }
  };

  const remove = async (site: Site) => {
    setBusy(`remove:${site.id}`);
    try { await api(`/api/ads/pixel/sites/${encodeURIComponent(site.id)}`, { method: "DELETE" }); setConfirmRemove(null); await load(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "The page could not be removed."); }
    finally { setBusy(""); }
  };

  // Live test: open the page in a new tab, then watch the account's events for
  // that hostname until the page view from this visit shows up.
  const startLiveTest = (site: Site) => {
    if (liveTimer.current) window.clearInterval(liveTimer.current);
    const startedAt = new Date(Date.now() - 60 * 1000).toISOString();
    setLiveTest({ hostname: site.hostname, startedAt, events: [], state: "waiting" });
    const deadline = Date.now() + 120 * 1000;
    const poll = async () => {
      try {
        const result = await api<{ events: RecentEvent[] }>(`/api/ads/pixel/recent?hostname=${encodeURIComponent(site.hostname)}&since=${encodeURIComponent(startedAt)}`);
        if (result.events.length > 0) {
          setLiveTest({ hostname: site.hostname, startedAt, events: result.events, state: "seen" });
          window.clearInterval(liveTimer.current); liveTimer.current = 0;
          void load();
          return;
        }
      } catch { /* keep polling until the deadline */ }
      if (Date.now() > deadline) { setLiveTest((current) => current && { ...current, state: "timeout" }); window.clearInterval(liveTimer.current); liveTimer.current = 0; }
    };
    liveTimer.current = window.setInterval(() => { void poll(); }, 5000);
  };

  const toggleHost = async (hostname: string) => {
    if (expanded === hostname) { setExpanded(null); return; }
    setExpanded(hostname);
    if (hostEvents[hostname]) return;
    setHostEvents((current) => ({ ...current, [hostname]: "loading" }));
    try { const result = await api<WebsiteEvents>(`/api/ads/pixel/website-events?hostname=${encodeURIComponent(hostname)}`); setHostEvents((current) => ({ ...current, [hostname]: result })); }
    catch { setHostEvents((current) => ({ ...current, [hostname]: "failed" })); }
  };

  if (error && !data) return <section className="px"><div className="surface-state surface-state--error"><strong>Pixel setup could not load</strong><span>{error}</span><button className="button button--secondary button--small" type="button" onClick={() => void load()}><RefreshCw /> Retry</button></div></section>;
  if (!data) return <section className="px px--loading"><LoaderCircle className="spin" /> Loading pixel status…</section>;

  const snippet = pixelSnippetPretty(data.account_id);
  const receiving = Boolean(data.status?.installed) && data.status?.last_seen_days !== null && data.status?.last_seen_days !== undefined;
  const fired = data.status?.last_fired_days || {};
  const firedEvents = CONVERSION_EVENTS.filter((event) => fired[event.name] !== undefined);
  const installedSites = data.sites.filter((site) => site.state === "installed").length;
  const step1Done = Boolean(data.status?.installed) || receiving || installedSites > 0;
  const step2Done = installedSites > 0 || (receiving && data.websites.length > 0);
  const step3Done = firedEvents.length > 0;
  const current = !step1Done ? 1 : !step2Done ? 2 : !step3Done ? 3 : 0;
  const open = openStep ?? current;
  const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;
  const guide = guideKey ? PIXEL_GUIDES.find((entry) => entry.key === guideKey) : undefined;
  const groups = Array.from(new Set(PIXEL_GUIDES.map((entry) => entry.group)));
  const selected = CONVERSION_EVENTS.find((event) => event.name === eventKey) ?? CONVERSION_EVENTS[0];
  const selectedCode = selected.snippet ?? trackSnippet(selected.name);
  const copy = (text: string, done: (value: boolean) => void) => { void navigator.clipboard.writeText(text).then(() => { done(true); window.setTimeout(() => done(false), 1600); }); };
  const mailto = `mailto:?subject=${encodeURIComponent("Pixel snippet for our ads")}&body=${encodeURIComponent(`Please add this snippet inside the <head> of every page we send ads to (landing pages, checkout, thank-you pages):\n\n${snippet}\n\nAfter it is live, let me know which pages you added it to.`)}`;

  const headTone = receiving ? "ok" : step1Done ? "wait" : "idle";
  const headTitle = receiving ? "Your pixel is live" : step1Done ? "Pixel found, waiting for the first visit" : "Your pixel is not installed yet";
  const headCopy = receiving
    ? `${plural(data.websites.length, "website")} sending data, last event ${daysAgo(data.status?.last_seen_days)}. ${firedEvents.length > 0 ? `${plural(firedEvents.length, "conversion event")} firing.` : "No conversion events yet."}`
    : step1Done ? "Open one of your pages in a browser to send the first event." : "Three short steps: install it, confirm a page, pick the conversion you pay for.";

  const toggle = (n: number) => setOpenStep((value) => (value ?? current) === n ? 0 : n);
  const Row = ({ n, title, status, tone, done }: { n: number; title: string; status: string; tone: "idle" | "wait" | "ok" | "warn"; done: boolean }) => (
    <button type="button" className="px-step__row" aria-expanded={open === n} onClick={() => toggle(n)}>
      <span className={`px-step__num ${done ? "is-done" : current === n ? "is-current" : ""}`}>{done ? <Check /> : n}</span>
      <span className="px-step__title">{title}</span>
      <StatusPill tone={tone}>{status}</StatusPill>
      <ChevronDown className="px-step__chev" />
    </button>
  );

  return <div className="pixel">
    {error && <div className="form-alert" role="alert">{error}</div>}

    <section className="px">
      <header className={`px-head is-${headTone}`}>
        <span className="px-head__dot" aria-hidden="true" />
        <div><strong>{headTitle}</strong><span>{headCopy}</span></div>
        <button className="pixel-iconbtn" type="button" onClick={() => void load()} aria-label="Refresh status" title="Refresh status"><RefreshCw /></button>
      </header>

      <div className={`px-step ${open === 1 ? "is-open" : ""}`}>
        <Row n={1} title="Install the pixel" status={receiving ? "Live" : step1Done ? "Detected" : "Not detected"} tone={receiving ? "ok" : step1Done ? "wait" : "idle"} done={step1Done} />
        {open === 1 && <div className="px-step__body">
          <p>Paste one snippet inside the <code>&lt;head&gt;</code> of every page your ads point to: landing pages, checkout, thank-you pages. Your account id is already in it.</p>
          <div className="px-actions">
            <button type="button" className="px-btn px-btn--primary" onClick={() => copy(snippet, setCopiedMain)}>{copiedMain ? <><Check /> Copied</> : <><Copy /> Copy snippet</>}</button>
            <label className="px-select">
              <select aria-label="Where are you pasting it?" value="" onChange={(event) => { if (event.target.value) setGuideKey(event.target.value); }}>
                <option value="">Where do I paste it?</option>
                {groups.map((group) => <optgroup key={group} label={group}>{PIXEL_GUIDES.filter((entry) => entry.group === group).map((entry) => <option key={entry.key} value={entry.key}>{entry.name}</option>)}</optgroup>)}
              </select>
            </label>
            <a className="px-btn" href={mailto}><Mail /> Send to a developer</a>
          </div>
          <details className="px-code">
            <summary>View the code</summary>
            <pre>{snippet}</pre>
          </details>
        </div>}
      </div>

      <div className={`px-step ${open === 2 ? "is-open" : ""}`}>
        <Row n={2} title="Confirm a page" status={data.sites.length === 0 ? "No pages yet" : `${installedSites} of ${data.sites.length} installed`} tone={step2Done ? "ok" : data.sites.length > 0 ? "warn" : "idle"} done={step2Done} />
        {open === 2 && <div className="px-step__body">
          <p>Paste the URL your ads point to. The page is read for your snippet and watched for its first visit.</p>
          <form className="pixel-urlbar" onSubmit={(event) => { event.preventDefault(); void checkUrl(); }}>
            <input value={urlDraft} onChange={(event) => setUrlDraft(event.target.value)} placeholder="yourbrand.com/offer" inputMode="url" aria-label="Page URL" />
            <button type="submit" disabled={checking || !urlDraft.trim()}>{checking ? <><LoaderCircle className="spin" /> Checking</> : "Check page"}</button>
          </form>
          {data.sites.length > 0 && <ul className="pixel-pages">
            {data.sites.map((site) => <li key={site.id} className={`is-${site.state}`}>
              <div className="pixel-pages__main">
                <div className="pixel-pages__title"><StateBadge state={site.state} /><a href={site.url} target="_blank" rel="noreferrer">{site.url.replace(/^https?:\/\//, "")}</a></div>
                <span className="pixel-pages__meta">Last event {daysAgo(site.last_seen_days)} · checked {formatWhen(site.checked_at) || "never"}</span>
                {site.state === "missing" && <small>The snippet was not found on this page. If you just added it, re-check in a minute.</small>}
                {site.state === "foreign" && <small>A pixel is on this page but it belongs to another account. Replace the id inside setScope with yours.</small>}
                {site.state === "unreachable" && <small>The page did not respond. Make sure it is public and not behind a login.</small>}
              </div>
              <div className="pixel-pages__actions">
                <button type="button" disabled={busy === `check:${site.id}`} onClick={() => void recheck(site)}>{busy === `check:${site.id}` ? "Checking…" : "Re-check"}</button>
                <a href={site.url} target="_blank" rel="noopener noreferrer" onClick={() => startLiveTest(site)}>Test live</a>
                <button type="button" className="is-danger" aria-label={`Remove ${site.url}`} aria-expanded={confirmRemove === site.id} onClick={() => setConfirmRemove((value) => value === site.id ? null : site.id)}><Trash2 /></button>
              </div>
              {confirmRemove === site.id && <div className="pixel-confirm" role="alertdialog" aria-label="Confirm removal">
                <span>Remove <strong>{site.url.replace(/^https?:\/\//, "")}</strong> from this list? It only removes the saved check here. Nothing changes on your website or in tracking.</span>
                <div><button type="button" className="button button--secondary button--small" onClick={() => setConfirmRemove(null)}>Keep it</button><button type="button" className="button button--small pixel-confirm__remove" disabled={busy === `remove:${site.id}`} onClick={() => void remove(site)}>{busy === `remove:${site.id}` ? "Removing…" : "Remove"}</button></div>
              </div>}
            </li>)}
          </ul>}
          {liveTest && <div className={`pixel-live is-${liveTest.state}`} role="status">
            {liveTest.state === "waiting" && <><LoaderCircle className="spin" /> {liveTest.hostname} opened in a new tab. Waiting for its page view to arrive, usually within a few minutes.</>}
            {liveTest.state === "seen" && <><Check /> Events from {liveTest.hostname} arrived: {liveTest.events.slice(0, 3).map((event) => `${eventLabel(event.event_name || "")}${event.path ? ` on ${event.path}` : ""}`).join(", ")}. The pixel works.</>}
            {liveTest.state === "timeout" && <>Nothing arrived from {liveTest.hostname} yet. Confirm the snippet is on that exact page and re-check in a few minutes.</>}
            <button type="button" onClick={() => { if (liveTimer.current) window.clearInterval(liveTimer.current); liveTimer.current = 0; setLiveTest(null); }}>Dismiss</button>
          </div>}
          {data.websites.length > 0 && <ul className="px-hosts">
            {data.websites.map((site) => <li key={site.hostname}>
              <button type="button" className="px-hosts__row" aria-expanded={expanded === site.hostname} onClick={() => void toggleHost(site.hostname)}>
                <strong>{site.hostname}</strong>
                <span>{site.page_views.toLocaleString()} page views · {site.visitors.toLocaleString()} visitors · last {site.last_seen ? site.last_seen.slice(5) : "none"}</span>
                <ChevronDown />
              </button>
              {expanded === site.hostname && <div className="px-hosts__detail">
                {hostEvents[site.hostname] === "loading" && <span className="pixel-empty"><LoaderCircle className="spin" /> Loading events…</span>}
                {hostEvents[site.hostname] === "failed" && <span className="pixel-empty">The events for this website could not be loaded.</span>}
                {typeof hostEvents[site.hostname] === "object" && ((hostEvents[site.hostname] as WebsiteEvents).events.length === 0
                  ? <span className="pixel-empty">Only page views so far.</span>
                  : (hostEvents[site.hostname] as WebsiteEvents).events.map((entry) => <span key={entry.event} className="px-hosts__event"><strong>{eventLabel(entry.event)}</strong> {entry.count.toLocaleString()}</span>))}
              </div>}
            </li>)}
          </ul>}
        </div>}
      </div>

      <div className={`px-step ${open === 3 ? "is-open" : ""}`}>
        <Row n={3} title="Pick your conversion" status={step3Done ? `${plural(firedEvents.length, "event")} firing` : "Nothing yet"} tone={step3Done ? "ok" : "idle"} done={step3Done} />
        {open === 3 && <div className="px-step__body">
          <p>Choose what you pay for. Ads optimize toward it once it starts arriving.</p>
          <div className="pixel-picker" role="tablist" aria-label="Conversion events">
            {CONVERSION_EVENTS.map((event) => <button key={event.name} role="tab" type="button" aria-selected={selected.name === event.name} className={`${selected.name === event.name ? "is-active" : ""} ${fired[event.name] !== undefined ? "is-fired" : ""}`} onClick={() => setEventKey(event.name)}><i aria-hidden="true" />{event.label}</button>)}
          </div>
          <div className="px-track">
            <code>{selectedCode}</code>
            <button type="button" className="px-btn px-btn--primary" onClick={() => copy(selectedCode, setCopiedEvent)}>{copiedEvent ? <><Check /> Copied</> : <><Copy /> Copy</>}</button>
          </div>
          <p className="px-hint">
            {selected.when} Add this call on the thank-you page (inside <code>&lt;script&gt;</code> tags, with the pixel snippet on that page too) or in the form's success handler.{selected.name === "purchase" && " Send the real order value."}
            {" "}<span className={fired[selected.name] !== undefined ? "is-fired" : ""}>{fired[selected.name] !== undefined ? `Fired ${daysAgo(fired[selected.name])}.` : "Not received yet."}</span>
          </p>
        </div>}
      </div>

      <footer className="px-foot">Event IDs, customer fields, and sending from your own server are in the <a href="/api-docs#pixel" target="_blank" rel="noreferrer">pixel reference</a>.</footer>
    </section>

    {guide && <div className="px-modal" role="presentation" onClick={(event) => { if (event.target === event.currentTarget) setGuideKey(null); }}>
      <div className="px-modal__card" role="dialog" aria-modal="true" aria-label={`Install on ${guide.name}`}>
        <header><div><strong>Install on {guide.name}</strong><span>Your account id is already filled into every snippet.</span></div><button type="button" className="pixel-iconbtn" aria-label="Close" onClick={() => setGuideKey(null)}><X /></button></header>
        <div className="px-modal__body"><GuideView guide={guide} accountId={data.account_id} /></div>
      </div>
    </div>}
  </div>;
}
